import React from "react";

/**
 * Roll Fast TMS — Global Error Boundary.
 *
 * Wraps role shells (AdminShell, DriverShell, CustomerShell, CarrierPortal)
 * so a single broken sub-tree never blanks the whole app. Captures the
 * error, logs it, posts it to the backend's audit endpoint (best-effort),
 * and renders an in-context fallback with a "Try again" button + a copy
 * link.
 *
 * Why not a hook? React's error-boundary contract still requires a class
 * component (componentDidCatch / getDerivedStateFromError).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Best-effort: send the error to the backend so we can spot regressions
    // in production. Never throws — we're already in an error path.
    this.setState({ info });
    try {
      const url = (process.env.REACT_APP_BACKEND_URL || "") + "/api/events/client-error";
      // We use sendBeacon-ish fetch (keepalive) so navigations don't drop it.
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          message: String(error?.message || error || "Unknown error"),
          stack: String(error?.stack || "").slice(0, 4000),
          component_stack: String(info?.componentStack || "").slice(0, 4000),
          path: typeof window !== "undefined" ? window.location.pathname : "",
          area: this.props.area || "unknown",
          ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
        }),
      }).catch(() => {});
    } catch (_) {
      // intentionally swallow
    }
    // Echo to console for the dev tools.
    console.error("[ErrorBoundary]", this.props.area, error, info);
  }

  handleReset = () => {
    this.setState({ error: null, info: null });
  };

  handleHardReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const area = this.props.area || "this page";
    return (
      <div
        role="alert"
        data-testid={`error-boundary-${this.props.area || "global"}`}
        className="min-h-[60vh] flex items-center justify-center p-6 bg-[#050505] text-white"
      >
        <div className="max-w-lg w-full rf-card-dark border border-red-900/60 p-6 sm:p-8 rounded-xl shadow-2xl">
          <div className="flex items-center gap-3 mb-3">
            <div
              aria-hidden="true"
              className="h-10 w-10 rounded-full bg-red-600/20 border border-red-600 flex items-center justify-center text-red-400 font-bold"
            >
              !
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold">Something went wrong</h1>
              <p className="text-xs text-neutral-400">
                A bug in {area} crashed this view. Your data is safe.
              </p>
            </div>
          </div>

          <pre className="mt-4 text-[11px] leading-snug text-red-300 bg-black/40 rounded-md p-3 overflow-auto max-h-40 whitespace-pre-wrap break-words">
            {String(error?.message || error)}
          </pre>

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={this.handleReset}
              data-testid="error-boundary-retry"
              className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-500 active:bg-red-700 text-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 focus:ring-offset-[#050505]"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleHardReload}
              data-testid="error-boundary-reload"
              className="px-4 py-2 rounded-md bg-neutral-800 hover:bg-neutral-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-neutral-400 focus:ring-offset-2 focus:ring-offset-[#050505]"
            >
              Reload page
            </button>
            <a
              href="/support"
              className="px-4 py-2 rounded-md border border-neutral-700 hover:border-neutral-500 text-sm self-center focus:outline-none focus:ring-2 focus:ring-neutral-400 focus:ring-offset-2 focus:ring-offset-[#050505]"
            >
              Contact support
            </a>
          </div>
        </div>
      </div>
    );
  }
}
